import React from 'react';
import styled, { keyframes } from 'styled-components';
import type { WorkspacePlan } from '../../../store/api/workspaceApi';

/* ---------- styled ---------- */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const PlanTimeline = styled.div<{ $compact?: boolean }>`
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  overflow-y: auto;
  flex: ${({ $compact }) => ($compact ? '0 0 auto' : '1 1 auto')};
  min-height: 0;
  max-height: ${({ $compact }) => ($compact ? '40vh' : 'none')};
  scrollbar-width: thin;
`;

const StepCard = styled.div<{ $status: string }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  font-size: 0.875rem;
  opacity: ${({ $status }) => ($status === 'pending' ? 0.6 : 1)};
`;

const StepIndicator = styled.span<{ $status: string }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  flex-shrink: 0;
  ${({ $status }) =>
    $status === 'completed'
      ? 'background: var(--success, #22c55e); color: white;'
      : $status === 'running'
        ? 'background: var(--accent); color: white;'
        : $status === 'failed'
          ? 'background: var(--danger, #ef4444); color: white;'
          : 'background: var(--bg-elevated); color: var(--text-muted); border: 1px solid var(--border);'}
`;

const StepInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const StepTitle = styled.div`
  font-weight: 500;
  color: var(--text-primary);
`;

const StepDesc = styled.div`
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin-top: 0.125rem;
`;

const CanvasFrame = styled.iframe`
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  border: none;
  background: var(--bg-primary, #0f172a);
`;

const EmptyCanvas = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: var(--text-muted);
  font-size: 0.9375rem;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const ProcessingCanvas = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: var(--text-muted);
  font-size: 0.9375rem;
`;

const ProcessingDots = styled.div`
  display: flex;
  gap: 6px;
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent, #3b82f6);
`;

const ProcessingLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

/* ---------- helpers ---------- */

function statusIcon(status: string): string {
  switch (status) {
    case 'completed': return '\u2713';
    case 'running':   return '\u25B6';
    case 'failed':    return '!';
    default:          return '\u2022';
  }
}

/* ---------- component ---------- */

interface ExploreCanvasProps {
  plan: WorkspacePlan | null;
  html: string | null;
  url: string | null;
  isProcessing: boolean;
  hasMessages: boolean;
}

const ExploreCanvas: React.FC<ExploreCanvasProps> = ({
  plan,
  html,
  url,
  isProcessing,
  hasMessages,
}) => {
  if (!plan && !html && !url) {
    if (isProcessing) {
      return (
        <ProcessingCanvas>
          <ProcessingDots>
            <Dot />
            <Dot />
            <Dot />
          </ProcessingDots>
          <ProcessingLabel>Analyzing your request and building a plan...</ProcessingLabel>
        </ProcessingCanvas>
      );
    }

    if (hasMessages) {
      return (
        <EmptyCanvas>
          <div style={{ fontSize: '2rem' }}>&#x1F4CB;</div>
          <div>Your plan will appear here once the agent has enough context</div>
        </EmptyCanvas>
      );
    }

    return (
      <EmptyCanvas>
        <div style={{ fontSize: '2rem' }}>&#x1F50D;</div>
        <div>Start a conversation to explore possibilities</div>
      </EmptyCanvas>
    );
  }

  return (
    <Wrapper>
      {plan && (
        <PlanTimeline $compact={Boolean(url || html)}>
          {plan.steps.map((step) => (
            <StepCard key={step.stepId} $status={step.status}>
              <StepIndicator $status={step.status}>
                {statusIcon(step.status)}
              </StepIndicator>
              <StepInfo>
                <StepTitle>{step.title}</StepTitle>
                <StepDesc>{step.description}</StepDesc>
              </StepInfo>
            </StepCard>
          ))}
        </PlanTimeline>
      )}

      {url ? (
        <CanvasFrame src={url} sandbox="allow-scripts" title="Explore canvas" />
      ) : html ? (
        <CanvasFrame srcDoc={html} sandbox="allow-scripts" title="Explore canvas" />
      ) : null}
    </Wrapper>
  );
};

export default ExploreCanvas;
